import { LitElement, html } from "lit";
import { customElement, state } from "lit/decorators.js";
import type { ContentPayload } from "./types";
import { loadContent } from "./services/content";
import { currentRoute, installLinkNavigation, type Route } from "./services/router";
import { suggestionBoardUrl } from "./services/suggestions";
import "./pages/home-page";
import "./pages/learn-page";
import "./pages/graph-page";
import "./pages/suggestions-page";
import "./pages/study-page";

@customElement("ghostbeats-app")
export class GhostbeatsApp extends LitElement {
  @state() private content?: ContentPayload;
  @state() private route: Route = currentRoute();
  @state() private error = "";
  private removeNavigation?: () => void;
  protected createRenderRoot() { return this; }

  connectedCallback(): void {
    super.connectedCallback();
    this.removeNavigation = installLinkNavigation();
    window.addEventListener("popstate", this.routeChanged);
    void loadContent().then((content) => { this.content = content; }).catch((error: Error) => { this.error = error.message; });
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.removeNavigation?.();
    window.removeEventListener("popstate", this.routeChanged);
  }

  private routeChanged = (): void => { this.route = currentRoute(); };

  private get suggestionsEnabled(): boolean {
    return Boolean(suggestionBoardUrl());
  }

  private get suggestionsHref(): string {
    const source = this.route.page === "suggestions" ? "/" : window.location.pathname;
    return `/suggestions?from=${encodeURIComponent(source)}`;
  }

  render() {
    return html`
      <div class="site-noise" aria-hidden="true"></div>
      <header class="site-header">
        <a class="brand" href="/" aria-label="Ghostbeats home"><span>GB</span><b>Ghostbeats</b></a>
        <nav><a class=${this.route.page === "learn" ? "active" : ""} href="/learn/meet-the-kit">Fundamentals</a><a class=${this.route.page === "graph" ? "active" : ""} href="/graph">Evolution graph</a><a class=${this.route.page === "study" ? "active" : ""} href="/graph">Song catalog</a>${this.suggestionsEnabled ? html`<a class=${this.route.page === "suggestions" ? "active" : ""} href=${this.suggestionsHref}>Suggestions</a>` : ""}</nav>
      </header>
      ${this.renderPage()}
      ${this.suggestionsEnabled && this.route.page !== "suggestions" ? html`<a class="suggest-launcher" href=${this.suggestionsHref}><span>＋</span> Suggest a song or feature</a>` : ""}
      <footer class="site-footer"><span>Ghostbeats · hear how metal moves</span><span>Interactive metal drum appreciation lessons</span></footer>
    `;
  }

  private renderPage() {
    if (this.error) return html`<main class="empty-state"><div class="eyebrow">Content error</div><h1>Ghostbeats could not start.</h1><p>${this.error}</p><button class="button primary" @click=${() => location.reload()}>Reload latest version</button></main>`;
    if (!this.content) return html`<main class="loading-state"><span></span><p>Loading the rhythmic family tree…</p></main>`;
    switch (this.route.page) {
      case "home": return html`<home-page .content=${this.content}></home-page>`;
      case "learn": return html`<learn-page .content=${this.content} .slug=${this.route.slug}></learn-page>`;
      case "graph": return html`<graph-page .content=${this.content}></graph-page>`;
      case "suggestions": return html`<suggestions-page .boardUrl=${suggestionBoardUrl()}></suggestions-page>`;
      case "study": return html`<study-page .content=${this.content} .slug=${this.route.slug}></study-page>`;
      default: return html`<main class="empty-state"><div class="eyebrow">404</div><h1>That beat is missing.</h1><a class="button primary" href="/">Return home</a></main>`;
    }
  }
}
